import { ServiceTypeOperationUpsertDialogStore } from './service-type-operation-upsert-dialog.store';
import { fb, translate } from '../../../../../modules/shared/shared.global-service';
import { Component, computed, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { CheckInputComponent } from '../../../../kit/input/check-input/check-input.component';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { socialServiceOperationEnumOptions } from '@libs/types/src/services/enum/social-service-operation.enum';
import {
    socialServiceOperationPaymentEnumOptions,
} from '@libs/types/src/services/enum/social-service-operation-payment.enum';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import {
    SocialServiceOperationCreateInput,
} from '@libs/types/src/services/input-type/services/social-service/social-service-operation.create-input';
import {
    SocialServiceOperationPatchInput,
} from '@libs/types/src/services/input-type/services/social-service/social-service-operation.patch-input';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { dateify } from '../../../../../modules/shared/helpers/date.helper';
import { nameColumn, textColumn } from '../../../../../modules/shared/components/kit/table/table.fields';
import {
    SocialServiceOperationObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-operation.object';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import {
    getSocialServiceOperationFrag,
    getSocialServiceOperationProcedureFrag,
} from './service-type-operation-upsert-dialog.query';
import { measurementUnitEnumOptions } from '@libs/types/src/common/enum/measurement-unit.enum';
import { ActionButtonsComponent } from '../../../../kit/action-buttons/action-buttons.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import {
    SocialServiceOperationProcedureObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-operation-procedure.object';
import {
    ServiceTypeOperationProcedureDialogComponent,
} from './service-type-operation-procedure-dialog/service-type-operation-procedure-dialog.component';
import {
    socialServiceFacultativeCodeEnumOptions,
} from '@libs/types/src/services/enum/social-service-facultative-code.enum';
import {
    socialServiceInformativeCodeEnumOptions,
} from '@libs/types/src/services/enum/social-service-informative-code.enum';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { TextAreaInputComponent } from '../../../../kit/input/text-area-input/text-area-input.component';

@Component({
    selector: 'app-service-type-operation-upsert-dialog',
    templateUrl: './service-type-operation-upsert-dialog.component.html',
    styleUrl: './service-type-operation-upsert-dialog.component.scss',
    providers: [ServiceTypeOperationUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextInputComponent,
        SelectInputComponent,
        CheckInputComponent,
        DateInputComponent,
        TooltipModule,
        ActionButtonsComponent,
        ButtonComponent,
        TextAreaInputComponent,
        TableComponent,
    ],
})
export class ServiceTypeOperationUpsertDialogComponent extends BaseDialogComponent {

    public readonly store = inject(ServiceTypeOperationUpsertDialogStore);
    public readonly confirmBuilder = inject(ConfirmBuilder);
    public readonly dialogService = inject(BaseDialogService);

    protected readonly procedureTable = viewChild<TableComponent>('procedureTable');

    protected prefix = 'serviceType.operation.';
    protected procedurePrefix = 'serviceType.operation.procedure.';

    protected form = fb.group({
        name: fb.control<string | null>(null, Validators.required),
        description: fb.control<string>(''),
        type: fb.control<string | null>(null, Validators.required),
        billCode: fb.control<string | null>(null),
        mpsvBasic: fb.control<boolean>(false),
        mpsvMeeting: fb.control<boolean>(false),
        noRecord: fb.control<boolean>(false),
        facultativeCode: fb.control<string | null>(null),
        informationCode: fb.control<string | null>(null),
        unit: fb.control<string | null>(null),
        payment: fb.control<string | null>(null),
        maxPrice: fb.control<string | null>(null),
        maxPriceExt: fb.control<string | null>(null),
        validFrom: fb.control<Date | null>(null),
        validTo: fb.control<Date | null>(null),
    });

    protected columns = [
        nameColumn,
        textColumn('winCarolCode'),
        textColumn('customCode'),
    ];

    // protected billCodeChange = changeSignal(this.form.controls.billCode);
    // protected typeChange = changeSignal(this.form.controls.type);
    protected operation = signal<SocialServiceOperationObject | null>(null);
    protected procedureFilter = computed(() => {
        const builder = new FilterBuilder();
        const op = this.operation();
        if (op) {
            builder.where('socialServiceOperationId', op.id);
        }

        return builder.filter().filter;
    });

    protected billCodes = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'p', 'y', 'z'].map((it) => {
        return {
            name: it,
            value: it,
        };
    });

    constructor() {
        super();

        const op = this.dialogConfig.data.operation;
        this.operation.set(op || null);
        if (op) {
            this.form.patchValue({
                ...op,
                validFrom: dateify(op.validFrom),
                validTo: dateify(op.validTo),
            });
        }
    }

    public override async save(continueEdit: any = false): Promise<void> {
        if (isInvalid(this.form)) return;

        const id = this.dialogConfig.data.operation?.id;
        const _data = {
            ...this.form.getRawValue(),
            description: this.form.value.description || '',
        } as any;
        let res: SocialServiceOperationObject | null = null;

        if (id) {
            const data = _data as SocialServiceOperationPatchInput;
            data.id = id;
            await this.store.api.patchSocialServiceOperation(data, 'id');
            contentGlobalSuccess.next('common.updated');
        } else {
            const data = _data as SocialServiceOperationCreateInput;
            data.serviceTypeId = this.store.serviceType().id;
            res = await this.store.api.createSocialServiceOperation(data, getSocialServiceOperationFrag);
            contentGlobalSuccess.next('common.created');
        }

        if (continueEdit) {
            this.operation.set(res || null);
        } else {
            this.dialogRef.close(true);
        }
    }

    protected async upsertProcedure(procedure: SocialServiceOperationProcedureObject | null = null): Promise<void> {
        this.dialogService.open(
            ServiceTypeOperationProcedureDialogComponent,
            {
                header: translate.instant('serviceType.operation.procedure.label'),
                data: { procedure, operation: this.operation() },
                styleClass: 'p-dialog-width-60',
                baseZIndex: 9050,
            },
        ).onClose.subscribe((it) => {
            if (it) {
                this.procedureTable()?.reload();
            }
        });
    }

    protected async deleteProcedure(event: any): Promise<void> {
        this.confirmBuilder.confirmDelete(
            async () => {
                await this.store.api.deleteSocialServiceOperationProcedure({ id: event.item.id });
                contentGlobalSuccess.next('common.deleted');
                this.procedureTable()?.reload();
            },
            event,
        );
    }

    protected readonly socialServiceOperationEnumOptions = socialServiceOperationEnumOptions;
    protected readonly socialServiceOperationPaymentEnumOptions = socialServiceOperationPaymentEnumOptions;

    protected readonly getSocialServiceOperationProcedureFrag = getSocialServiceOperationProcedureFrag;
    protected readonly measurementUnitEnumOptions = measurementUnitEnumOptions;
    protected readonly socialServiceFacultativeCodeEnumOptions = socialServiceFacultativeCodeEnumOptions;
    protected readonly socialServiceInformativeCodeEnumOptions = socialServiceInformativeCodeEnumOptions;
}
